import { z } from 'zod';
import { paginationQuerySchema } from '../common/pagination.js';
import { parsePhone } from '../phone/phone.js';
import { isIsoCountryCode } from './countries.js';
export * from './countries.js';
/**
 * Tablet service contracts (docs/rebuild/tablet.md). The platform API
 * (section 2) is consumed by other Al-Musafer platforms; keep it stable.
 */
// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------
export const TASK_LANGUAGES = ['ar', 'en'];
export const taskLanguageSchema = z.enum(TASK_LANGUAGES);
export const recordStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);
export const systemCodeSchema = z
    .string()
    .trim()
    .min(2)
    .max(40)
    .regex(/^[A-Z][A-Z0-9_]*$/, 'must use upper-case letters, digits and underscores');
/** A bilingual label: `{ ar, en }`, both required. */
export const bilingualTextSchema = (max) => z.object({
    ar: z.string().trim().min(1).max(max),
    en: z.string().trim().min(1).max(max),
});
export const TASK_STATUSES = ['SENT', 'OPENED', 'SUBMITTED', 'CANCELLED', 'EXPIRED'];
export const taskStatusSchema = z.enum(TASK_STATUSES);
/** A task is open, and the tablet busy with it, while SENT or OPENED. */
export const OPEN_TASK_STATUSES = ['SENT', 'OPENED'];
export const TASK_FIELD_TYPES = [
    'text',
    'code',
    'phone',
    'select',
    'country',
    'switch',
    'photo',
];
export const taskFieldTypeSchema = z.enum(TASK_FIELD_TYPES);
/** Section 2.2: a form to fill in, or one QR code to read (section 2.8). */
export const TASK_KINDS = ['form', 'scan'];
export const taskKindSchema = z.enum(TASK_KINDS);
/** Section 2.2: a paired tablet, or a one-time link on the employee's screen (section 2.9). */
export const TASK_TARGETS = ['tablet', 'link'];
export const taskTargetSchema = z.enum(TASK_TARGETS);
export const MAX_SUMMARY_LINES = 12;
export const MAX_SUMMARY_VALUE_LENGTH = 200;
export const MAX_FIELDS = 20;
export const MAX_PHOTO_FIELDS = 2;
export const MAX_SECTIONS = 20;
export const MAX_FIELD_LENGTH = 500;
/** Default maxLength of a `text` field (section 2.2). */
export const DEFAULT_TEXT_MAX_LENGTH = 200;
/** Default maxLength of a `code` field (section 2.2). */
export const DEFAULT_CODE_MAX_LENGTH = 64;
export const MIN_SELECT_OPTIONS = 2;
export const MAX_SELECT_OPTIONS = 50;
export const MAX_DECLARATION_LENGTH = 2000;
/** Section 2.8: the value a scan task returns. */
export const MAX_SCANNED_VALUE_LENGTH = 200;
/** Section 2.9: the address the link page returns to. */
export const MAX_RETURN_URL_LENGTH = 2000;
/** Section 5.3: allowed return addresses per connected system. */
export const MAX_ALLOWED_RETURN_ORIGINS = 20;
/** The signature PNG the tablet uploads, at most 512 KB once decoded. */
export const SIGNATURE_MAX_BYTES = 512 * 1024;
/** Section 2.7: a photo field's JPEG, at most 5 MB. */
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
export const PHOTO_CONTENT_TYPE = 'image/jpeg';
/** Section 2.2: the tablet scales a photo to at most this many pixels per side. */
export const PHOTO_MAX_DIMENSION_PX = 1600;
/** The organisation logo, PNG or JPEG, at most 512 KB once decoded. */
export const LOGO_MAX_BYTES = 512 * 1024;
export const LOGO_CONTENT_TYPES = ['image/png', 'image/jpeg'];
/** The header carrying a link task's token on the link page's routes (section 2.9). */
export const LINK_TOKEN_HEADER = 'x-link-token';
/** The HttpOnly cookie holding a paired tablet's device token. */
export const TABLET_DEVICE_COOKIE = 'tbl_device';
export const SSE_EVENTS = {
    /** The tablet's current task, or null for the idle screen. */
    tabletTask: 'task',
    /** The tablet was disabled: it returns to pairing. */
    tabletRevoked: 'revoked',
    /** A heartbeat the tablet uses to notice a silently dropped connection. */
    ping: 'ping',
};
// ---------------------------------------------------------------------------
// Canonical JSON and the data hash (section 2.3)
// ---------------------------------------------------------------------------
/**
 * Canonical JSON: object keys sorted by UTF-16 code unit (JavaScript's
 * default sort), no whitespace, `undefined` members dropped. Platforms that
 * recompute `dataSha256` must serialise the same way.
 */
export function canonicalJson(value) {
    if (value === null || typeof value !== 'object')
        return JSON.stringify(value) ?? 'null';
    if (Array.isArray(value))
        return `[${value.map((item) => canonicalJson(item)).join(',')}]`;
    const keys = Object.keys(value)
        .filter((key) => value[key] !== undefined)
        .toSorted();
    return `{${keys
        .map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`)
        .join(',')}}`;
}
/**
 * The data the signature belongs to (section 2.3): the title and summary as
 * sent, the fields as submitted, the declaration in the language shown, and
 * that language. `dataSha256` is the SHA-256 (hex) of `canonicalJson` of
 * this object. A scan task adds its `scannedValue`; a form task has none, so
 * its hash is unchanged from version 1.0 (canonicalJson drops undefined).
 * A photo field's submitted value is the photo's SHA-256 (section 2.7).
 */
export function signedDataOf(payload, submission) {
    return {
        title: payload.title,
        summary: payload.summary,
        fields: submission.fields,
        declaration: payload.declaration ? payload.declaration[submission.language] : null,
        language: submission.language,
        scannedValue: submission.scannedValue,
    };
}
// ---------------------------------------------------------------------------
// Platform API (server to server) - section 2
// ---------------------------------------------------------------------------
export const tabletsOfBranchQuerySchema = z.object({
    branchId: z.string().trim().min(1).max(64),
});
const keySchema = z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, 'must start with a letter and use letters, digits and _');
/** A summary value: one string, or `{ar,en}` when it is formatted per language. */
export const localizedValueSchema = z.union([
    z.string().trim().min(1).max(MAX_SUMMARY_VALUE_LENGTH),
    bilingualTextSchema(MAX_SUMMARY_VALUE_LENGTH),
]);
export const taskSummaryLineSchema = z.object({
    label: bilingualTextSchema(120),
    value: localizedValueSchema,
});
export const taskFieldOptionSchema = z.object({
    value: z.string().trim().min(1).max(100),
    label: bilingualTextSchema(120),
});
/** Section 2.2: a group of fields, shown only while `visibleWhen` holds. */
export const taskSectionSchema = z.object({
    key: keySchema,
    title: bilingualTextSchema(120),
    hint: bilingualTextSchema(300).optional(),
    visibleWhen: z
        .object({
        field: keySchema,
        equals: z.string().trim().min(1).max(100),
    })
        .optional(),
});
/** The longest value a field of this type accepts. */
export function maxLengthOf(field) {
    if (field.maxLength)
        return field.maxLength;
    return field.type === 'code' ? DEFAULT_CODE_MAX_LENGTH : DEFAULT_TEXT_MAX_LENGTH;
}
export const taskFieldSchema = z
    .object({
    key: keySchema,
    type: taskFieldTypeSchema,
    label: bilingualTextSchema(120),
    value: z.string().trim().max(MAX_FIELD_LENGTH).nullable().optional(),
    required: z.boolean().optional(),
    readOnly: z.boolean().optional(),
    maxLength: z.number().int().min(1).max(MAX_FIELD_LENGTH).optional(),
    /** The section this field belongs to (section 2.2). */
    section: keySchema.optional(),
    /** `select` only: 2-50 options. */
    options: z.array(taskFieldOptionSchema).optional(),
    /** `select` only: show the options side by side. */
    display: z.literal('radio').optional(),
})
    .superRefine((field, ctx) => {
    const value = field.value ?? null;
    const issue = (path, message) => ctx.addIssue({ code: 'custom', path: [path], message });
    if (field.readOnly && (value === null || value === '')) {
        issue('value', 'a readOnly field needs a value');
    }
    if (field.type === 'phone' && value) {
        // The tablet service cannot know what a calling system stores, so a
        // phone field never carries a number of an excluded country (customers.md
        // section 9.7, tablet.md section 3): an existing one is shown as
        // display-only text instead.
        const phone = parsePhone(value);
        if (!phone.ok && phone.error === 'PHONE_COUNTRY_NOT_ALLOWED') {
            // The stable code is the message, the one place a Standard Schema
            // issue keeps it; the validation pipe reports it as the field code.
            ctx.addIssue({
                code: 'custom',
                path: ['value'],
                message: 'PHONE_COUNTRY_NOT_ALLOWED',
                params: { code: 'PHONE_COUNTRY_NOT_ALLOWED' },
            });
        }
        else if (!phone.ok || phone.e164 !== value) {
            issue('value', 'must be the E.164 of a mobile');
        }
    }
    if (field.maxLength && field.type !== 'text' && field.type !== 'code') {
        issue('maxLength', 'applies only to text and code fields');
    }
    if ((field.type === 'text' || field.type === 'code') && value) {
        if (value.length > maxLengthOf(field))
            issue('value', 'is longer than maxLength');
    }
    if (field.type === 'select') {
        const options = field.options ?? [];
        if (options.length < MIN_SELECT_OPTIONS || options.length > MAX_SELECT_OPTIONS) {
            issue('options', `a select field needs ${MIN_SELECT_OPTIONS}-${MAX_SELECT_OPTIONS} options`);
        }
        else if (new Set(options.map((option) => option.value)).size !== options.length) {
            issue('options', 'option values must be unique');
        }
        if (value && !options.some((option) => option.value === value)) {
            issue('value', 'must be one of the options');
        }
    }
    else {
        if (field.options)
            issue('options', 'applies only to select fields');
        if (field.display)
            issue('display', 'applies only to select fields');
    }
    if (field.type === 'country' && value && !isIsoCountryCode(value)) {
        issue('value', 'must be an ISO 3166-1 alpha-2 country code');
    }
    if (field.type === 'switch' && value && value !== 'true' && value !== 'false') {
        issue('value', 'must be "true" or "false"');
    }
    if (field.type === 'photo') {
        if (value)
            issue('value', 'a photo field cannot be pre-filled');
        if (field.readOnly)
            issue('readOnly', 'a photo field cannot be read-only');
    }
});
const returnUrlSchema = z
    .string()
    .trim()
    .max(MAX_RETURN_URL_LENGTH)
    .refine((value) => {
    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
    }
    catch {
        return false;
    }
}, 'must be an absolute http or https address');
/**
 * Version 1.0 bodies (Currency) stay valid and hash the same: `kind`,
 * `target`, `confirmSummary`, `sections` and `returnUrl` have no defaults
 * here, so an older body parses to exactly what it parsed to before. The
 * service reads a missing `kind` as `form` and a missing `target` as `tablet`.
 */
export const createTaskSchema = z
    .object({
    kind: taskKindSchema.optional(),
    target: taskTargetSchema.optional(),
    tabletId: z.string().trim().min(1).max(64).optional(),
    branchId: z.string().trim().min(1).max(64).optional(),
    returnUrl: returnUrlSchema.optional(),
    idempotencyKey: z.string().trim().min(1).max(200),
    title: bilingualTextSchema(200),
    summary: z.array(taskSummaryLineSchema).max(MAX_SUMMARY_LINES).default([]),
    confirmSummary: z.boolean().optional(),
    sections: z.array(taskSectionSchema).max(MAX_SECTIONS).optional(),
    fields: z.array(taskFieldSchema).max(MAX_FIELDS).default([]),
    declaration: bilingualTextSchema(MAX_DECLARATION_LENGTH).nullable().optional(),
    signature: z.boolean().optional(),
    language: taskLanguageSchema.default('ar'),
})
    .superRefine((input, ctx) => {
    const issue = (path, message) => ctx.addIssue({ code: 'custom', path, message });
    const target = input.target ?? 'tablet';
    if (target === 'tablet') {
        if (!input.tabletId)
            issue(['tabletId'], 'is required for a tablet task');
        if (!input.branchId)
            issue(['branchId'], 'is required for a tablet task');
        if (input.returnUrl)
            issue(['returnUrl'], 'applies only to a link task');
    }
    else {
        if (input.tabletId)
            issue(['tabletId'], 'a link task has no tablet');
        if (!input.returnUrl)
            issue(['returnUrl'], 'is required for a link task');
    }
    if ((input.kind ?? 'form') === 'scan') {
        if (input.fields.length > 0)
            issue(['fields'], 'a scan task has no fields');
        if (input.sections?.length)
            issue(['sections'], 'a scan task has no sections');
        if (input.declaration)
            issue(['declaration'], 'a scan task has no declaration');
        if (input.signature)
            issue(['signature'], 'a scan task has no signature');
        if (input.confirmSummary)
            issue(['confirmSummary'], 'a scan task has no form');
        return;
    }
    const fieldsByKey = new Map(input.fields.map((field) => [field.key, field]));
    if (fieldsByKey.size !== input.fields.length)
        issue(['fields'], 'field keys must be unique');
    if (input.fields.filter((field) => field.type === 'photo').length > MAX_PHOTO_FIELDS) {
        issue(['fields'], `at most ${MAX_PHOTO_FIELDS} photo fields`);
    }
    const sections = input.sections ?? [];
    const sectionsByKey = new Map(sections.map((section) => [section.key, section]));
    if (sectionsByKey.size !== sections.length)
        issue(['sections'], 'section keys must be unique');
    input.fields.forEach((field, index) => {
        if (field.section && !sectionsByKey.has(field.section)) {
            issue(['fields', index, 'section'], 'is not a section of this task');
        }
    });
    sections.forEach((section, index) => {
        const condition = section.visibleWhen;
        if (!condition)
            return;
        const path = ['sections', index, 'visibleWhen'];
        const field = fieldsByKey.get(condition.field);
        if (!field || (field.type !== 'switch' && field.type !== 'select')) {
            issue([...path, 'field'], 'must name a switch or select field of this task');
            return;
        }
        // One level only: the controlling field is always shown.
        if (field.section && sectionsByKey.get(field.section)?.visibleWhen) {
            issue([...path, 'field'], 'must not be inside a section that can be hidden');
        }
        const allowed = field.type === 'switch'
            ? ['true', 'false']
            : (field.options ?? []).map((option) => option.value);
        if (!allowed.includes(condition.equals)) {
            issue([...path, 'equals'], 'is not a value of that field');
        }
    });
});
/** The value a switch shows when nothing pre-fills it. */
export const SWITCH_DEFAULT_VALUE = 'false';
/**
 * Section 2.2: the keys of the fields a hidden section hides, given the
 * current values (`visibleWhen` compares the controlling field's value).
 * The tablet and the server use this same rule.
 */
export function hiddenFieldKeys(task, values) {
    const hiddenSections = new Set();
    for (const section of task.sections ?? []) {
        const condition = section.visibleWhen;
        if (!condition)
            continue;
        const field = task.fields.find((candidate) => candidate.key === condition.field);
        const raw = values[condition.field] ?? null;
        const value = raw ?? (field?.type === 'switch' ? SWITCH_DEFAULT_VALUE : null);
        if (value !== condition.equals)
            hiddenSections.add(section.key);
    }
    return new Set(task.fields
        .filter((field) => field.section && hiddenSections.has(field.section))
        .map((field) => field.key));
}
export const claimPairingSchema = z.object({
    pairingSecret: z.string().min(1).max(200),
});
export const tabletSubmissionSchema = z.object({
    language: taskLanguageSchema,
    /** Every field's value by key; a read-only field may be omitted. */
    fields: z.record(z.string(), z.string().max(MAX_FIELD_LENGTH).nullable()).default({}),
    /** The signature PNG as standard base64, without a data: prefix. */
    signaturePngBase64: z
        .string()
        .max(Math.ceil(SIGNATURE_MAX_BYTES / 3) * 4 + 4)
        .regex(/^[A-Za-z0-9+/]*={0,2}$/, 'must be standard base64')
        .nullable()
        .optional(),
    /** Scan tasks only: the QR code's text (section 2.8). */
    scannedValue: z.string().min(1).max(MAX_SCANNED_VALUE_LENGTH).optional(),
});
// ---------------------------------------------------------------------------
// Admin: tablets - section 5.1
// ---------------------------------------------------------------------------
export const tabletListQuerySchema = paginationQuerySchema.extend({
    branchId: z.string().trim().min(1).max(64).optional(),
    status: recordStatusSchema.optional(),
});
const pairingCodeSchema = z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'must be the six-digit pairing code');
export const pairTabletSchema = z.object({
    code: pairingCodeSchema,
    name: z.string().trim().min(1).max(80),
    branchId: z.string().trim().min(1).max(64),
});
/**
 * Disabling revokes the device token (section 5.1), so enabling pairs the
 * tablet again: the code the device now shows is required.
 */
export const changeTabletStatusSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('INACTIVE') }),
    z.object({ status: z.literal('ACTIVE'), code: pairingCodeSchema }),
]);
export const updateTabletSchema = z.object({
    name: z.string().trim().min(1).max(80),
    branchId: z.string().trim().min(1).max(64),
});
// ---------------------------------------------------------------------------
// Admin: organisation details - section 5.2
// ---------------------------------------------------------------------------
export const updateOrganisationSchema = z.object({
    nameAr: z.string().trim().min(1).max(120),
    nameEn: z.string().trim().min(1).max(120),
});
export const uploadLogoSchema = z.object({
    contentType: z.enum(LOGO_CONTENT_TYPES),
    dataBase64: z
        .string()
        .min(4)
        .max(Math.ceil(LOGO_MAX_BYTES / 3) * 4 + 4, 'must be at most 512 KB')
        .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'must be standard base64'),
});
// ---------------------------------------------------------------------------
// Admin: connected systems - section 5.3
// ---------------------------------------------------------------------------
export const changeRecordStatusSchema = z.object({ status: recordStatusSchema });
/**
 * One allowed return address (section 5.3): an http or https origin, such
 * as `https://transfers.example.com` or `http://localhost:5240`, with no
 * path, query or fragment. A trailing slash is dropped.
 */
export const returnOriginSchema = z
    .string()
    .trim()
    .max(200)
    .refine((value) => {
    try {
        const url = new URL(value);
        return ((url.protocol === 'http:' || url.protocol === 'https:') &&
            url.pathname === '/' &&
            !url.search &&
            !url.hash &&
            !url.username &&
            !url.password &&
            !value.includes('?') &&
            !value.includes('#'));
    }
    catch {
        return false;
    }
}, 'must be an origin such as https://example.com, without a path')
    .transform((value) => new URL(value).origin);
/** True when `returnUrl` lies on one of the allowed origins (section 2.9). */
export function isAllowedReturnUrl(returnUrl, allowedOrigins) {
    try {
        return allowedOrigins.includes(new URL(returnUrl).origin);
    }
    catch {
        return false;
    }
}
export const allowedReturnUrlsSchema = z
    .array(returnOriginSchema)
    .max(MAX_ALLOWED_RETURN_ORIGINS)
    .transform((origins) => [...new Set(origins)]);
export const createSystemSchema = z.object({
    code: systemCodeSchema,
    nameAr: z.string().trim().min(1).max(120),
    nameEn: z.string().trim().min(1).max(120),
    /** عناوين الرجوع المسموحة (section 5.3). */
    allowedReturnUrls: allowedReturnUrlsSchema.optional(),
});
/** code is fixed after creation. */
export const updateSystemSchema = createSystemSchema.omit({ code: true }).partial();
export const systemListQuerySchema = paginationQuerySchema.extend({
    status: recordStatusSchema.optional(),
});
//# sourceMappingURL=contract.js.map